import { useCallback } from "react";

import type { SalesAnalyticsDispatcher } from "../../../../../sales-context/SalesContext.types";
import type { SessionSummary } from "../../../../../types";

import { useSalesContext } from "../../../../../sales-context/SalesContext";

const NO_SUMMARY_AVAILABLE_MESSAGE = "No summary available from past 30 days.";

export interface UsePreviousSessionSummaryParams {
    sessionId: string;
    isPreviousSessionSummaryEnabled: boolean;
    isChat: boolean;
    rootDispatcher: SalesAnalyticsDispatcher;
}

export interface UsePreviousSessionSummaryReturn {
    /**
     * Adds a previous session summary to the timeline.
     * If no summary is provided, shows "No summary available" message.
     * Automatically deactivates any existing summary first.
     */
    addToTimeline: (prevSummary?: SessionSummary) => void;
}

export function usePreviousSessionSummary(params: UsePreviousSessionSummaryParams): UsePreviousSessionSummaryReturn {
    const { sessionId, isPreviousSessionSummaryEnabled, isChat, rootDispatcher } = params;
    const { timeline, analytics } = useSalesContext();

    const addToTimeline = useCallback(
        (prevSummary?: SessionSummary) => {
            if (!isPreviousSessionSummaryEnabled || isChat || !timeline) return;

            const messages = timeline.getMessages(sessionId);
            const lastPrevSummaryMessage = messages.findLast((msg) => msg.type === "SessionSummary");

            const lastSummary = lastPrevSummaryMessage?.metadata?.sessionSummary;
            const shouldHideLastPrevSummaryMessage =
                lastSummary && lastPrevSummaryMessage?.text !== timeline.deactivatedSummaryMessage;

            if (shouldHideLastPrevSummaryMessage) {
                void rootDispatcher.dispatchBusinessEvent("PreviousSessionSummaryHidden", {
                    hiddenReason: "mdnUpdate",
                    newSummaryExists: !!prevSummary,
                });
                timeline.deactivatePreviousSessionSummaryMessage(sessionId, lastSummary.timestamp);
            }

            const header = "Previous session";
            const sessionType = "previous";

            if (!prevSummary || !prevSummary.summary) {
                timeline.addSessionSummaryMessage(sessionId, {
                    header,
                    summary: NO_SUMMARY_AVAILABLE_MESSAGE,
                    sessionType,
                    timestamp: new Date().toString(),
                });
                return;
            }

            const { summary, timestamp, resolutionStatus, callSid } = prevSummary;

            void rootDispatcher.dispatchBusinessEvent("PreviousSessionSummaryShown", {
                previousSummaryTimestamp: timestamp,
            });

            analytics.trackFullStoryEvent("PreviousSessionSummaryShown", { previousSummaryTimestamp: timestamp });

            timeline.addSessionSummaryMessage(sessionId, {
                header,
                summary,
                timestamp,
                resolutionStatus,
                sessionType,
                previousSessionId: callSid,
            });
        },
        [sessionId, isPreviousSessionSummaryEnabled, isChat, rootDispatcher, timeline, analytics],
    );

    return {
        addToTimeline,
    };
}
